/** DATELE PROIECTULUI pentru asistentul planului de situație (`citeste_planul`):
 *  indicatorii urbanistici, terenul, apartamentele, parcarea și abaterile, din
 *  ce e deja pe ecran — stratul sitului (`toSiteLayer`, aceleași câmpuri ca
 *  «Indicatori») și rezumatul soluției (`positioning_plan/service._summary`).
 *
 *  LIMITA ȘI REALIZATUL sunt două lucruri diferite și se scriu separat:
 *  `maxim_admis_pe_proiect` e plafonul din regulamentul proiectului (PUZ/CU),
 *  același pe toate soluțiile; `realizat_pe_solutia_deschisa` e cât consumă
 *  soluția de pe ecran. Un singur «cut: 2,4» l-ar lăsa pe model să le confunde. */
import type { SiteLayer } from "../client";
import type { Indicatori } from "../plan/siteGlyphs";

type Pereche = { valoare?: number; limita?: number } | undefined;

const indicator = (x: Pereche) => x && (x.valoare !== undefined || x.limita !== undefined)
  ? { realizat_pe_solutia_deschisa: x.valoare ?? null, maxim_admis_pe_proiect: x.limita ?? null }
  : undefined;

const mp = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? Math.round(v) : undefined);

export function datelePlanului(site: SiteLayer | null | undefined, summary: unknown): Record<string, unknown> {
  // tipul complet e al cardului «Indicatori» (desfășurata, comercialul), din același câmp
  const ind = site?.indicatori as Indicatori | undefined;
  const a = site?.areas ?? {};
  const s = (summary ?? {}) as Record<string, unknown>;
  const curat = (o: Record<string, unknown>) =>
    Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null));
  return curat({
    explicatie: "maxim_admis_pe_proiect = plafonul din regulamentul proiectului, același pe toate "
      + "soluțiile; realizat_pe_solutia_deschisa = cât atinge soluția de pe ecran. Ariile sunt în m².",
    indicatori: ind ? curat({
      pot: indicator(ind.pot),
      cut: indicator(ind.cut),
      niveluri: ind.niveluri && curat({
        realizat_pe_solutia_deschisa: ind.niveluri.valoare, regim: ind.niveluri.regim,
        maxim_admis_pe_proiect: ind.niveluri.limita }),
      desfasurata_supraterana_mp: mp(ind.supraterana),
      desfasurata_subterana_mp: mp(ind.subterana),
      comercial_mp: mp(ind.comercial),
      comercial_pct_din_desfasurata: ind.comercial_pct,
      zone_de_regim: ind.zone?.map((z) => curat({
        nume: z.nume, teren_mp: mp(z.teren), amprenta_mp: mp(z.amprenta),
        desfasurata_supraterana_mp: mp(z.supraterana),
        pot: indicator(z.pot), cut: indicator(z.cut), niveluri: indicator(z.niveluri),
      })),
    }) : undefined,
    teren: curat({
      lot_mp: mp(a.lot),
      verde_total_mp: mp(a.green_total ?? ((a.deep ?? 0) + (a.over_slab ?? 0) || undefined)),
      verde_pe_sol_mp: mp(a.deep), verde_peste_subsol_mp: mp(a.over_slab),
      sapatura_mp: mp(a.excavation),
    }),
    cladiri: curat({
      apartamente: s.apartamente, corpuri: s.corpuri, tronsoane: s.tronsoane,
      corpuri_functiune: s.corpuri_functiune, niveluri: s.niveluri,
    }),
    parcare: s.parcare,
    abateri_de_la_reguli: Array.isArray(s.abateri) && s.abateri.length ? s.abateri : undefined,
  });
}
